// components/integrations/tickets/TicketConfig.tsx
import React, { useState, useEffect } from 'react';
import { Settings, Globe, Link, Check, Copy, Save, AlertCircle, ExternalLink } from 'lucide-react';
import useModuleConfig from '../../../hooks/features/modules/useModuleConfig';
import { toast } from 'react-hot-toast';

interface TicketConfigProps {
  moduleName: string;
  isActive: boolean;
}

export const TicketConfig: React.FC<TicketConfigProps> = ({ moduleName, isActive }) => {
  const {
    configs,
    isLoading,
    error,
    getConfig,
    setConfig,
    loadConfigs,
  } = useModuleConfig({
    moduleName,
    autoLoad: true
  });

  const [trackingUrl, setTrackingUrl] = useState<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [copied, setCopied] = useState<string | null>(null);

  // Charger la config existante
  useEffect(() => {
    if (configs) {
      setTrackingUrl(getConfig('tracking_url') || '');
    }
  }, [configs, getConfig]);

  const handleSave = async () => {
    if (!trackingUrl.trim()) {
      toast.error('Veuillez saisir une URL valide');
      return;
    }

    // Validation basique
    try {
      new URL(trackingUrl);
    } catch {
      toast.error('URL invalide. Exemple: https://support.ma-entreprise.com/ticket');
      return;
    }

    setIsSaving(true);
    try {
      await setConfig('tracking_url', trackingUrl.trim());
      toast.success('Configuration mise à jour avec succès');
      await loadConfigs();
    } catch (err: any) {
      toast.error(err?.message || 'Erreur lors de la mise à jour');
    } finally {
      setIsSaving(false);
    }
  };

  const copyToClipboard = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  const getPreviewUrl = (): string => {
    if (!trackingUrl.trim()) return '';
    const baseUrl = trackingUrl.trim().replace(/[?&]+$/, '');
    const separator = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}ref=TICKET-XXXXXX`;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-600 text-sm">
        <AlertCircle size={16} className="inline mr-2" />
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Config : URL de tracking */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="border-b border-slate-200 p-5 bg-gradient-to-r from-indigo-50 to-purple-50">
          <div className="flex items-center gap-3">
            <Settings size={20} className="text-indigo-600" />
            <h3 className="font-semibold text-slate-800">Paramètres de suivi</h3>
            <span className={`ml-auto text-xs px-2.5 py-1 rounded-full font-medium ${
              isActive ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'
            }`}>
              {isActive ? '✅ Module actif' : '⏸️ Module inactif'}
            </span>
          </div>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-slate-600">
            Personnalisez l'URL vers laquelle vos clients seront redirigés pour suivre l'avancement de leurs tickets.
            {!isActive && (
              <span className="block mt-1 text-amber-600">
                ⚠️ Le module doit être activé pour utiliser cette fonctionnalité.
              </span>
            )}
          </p>

          {/* Champ URL */}
          <div>
            <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">
              URL de suivi personnalisée
            </label>
            <div className="flex items-center gap-2 mt-1.5">
              <div className="flex-1 relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                  <Link size={16} />
                </div>
                <input
                  type="url"
                  value={trackingUrl}
                  onChange={(e) => setTrackingUrl(e.target.value)}
                  placeholder="https://support.ma-entreprise.com/ticket"
                  className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors disabled:opacity-50"
                  disabled={!isActive || isSaving}
                />
              </div>
              <button
                onClick={handleSave}
                disabled={!isActive || isSaving || !trackingUrl.trim()}
                className="px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm font-medium"
              >
                {isSaving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Enregistrer
                  </>
                )}
              </button>
            </div>
            <p className="text-xs text-slate-400 mt-1.5">
              Laissez vide pour utiliser l'URL par défaut d'Izi Call Center.
            </p>
          </div>

          {/* Aperçu */}
          {trackingUrl.trim() && (
            <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                Aperçu du lien envoyé
              </label>
              <div className="flex items-center gap-2 mt-1.5">
                <code className="flex-1 bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm font-mono text-slate-700 truncate">
                  {getPreviewUrl()}
                </code>
                <button
                  onClick={() => copyToClipboard(getPreviewUrl(), 'preview')}
                  className="p-2.5 bg-white hover:bg-slate-100 rounded-lg transition-colors border border-slate-200"
                  title="Copier l'aperçu"
                >
                  {copied === 'preview' ? <Check size={18} className="text-green-600" /> : <Copy size={18} className="text-slate-600" />}
                </button>
                {getPreviewUrl() && (
                  <a
                    href={getPreviewUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2.5 bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors text-white"
                    title="Tester le lien"
                  >
                    <ExternalLink size={18} />
                  </a>
                )}
              </div>
              <p className="text-xs text-slate-400 mt-1.5">
                Le paramètre <code className="bg-slate-200 px-1.5 py-0.5 rounded text-xs">ref</code> est automatiquement ajouté avec la référence du ticket.
              </p>
            </div>
          )}

          {/* URL par défaut */}
          {!trackingUrl.trim() && (
            <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Globe size={16} className="text-slate-400" />
                <span>
                  URL par défaut : <code className="bg-white px-2 py-0.5 rounded text-xs font-mono">https://izicallcenter.com/ticket/tracking?ref=TICKET-XXXXXX</code>
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Documentation rapide */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle size={18} className="text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-medium text-blue-800">Comment ça fonctionne ?</h4>
            <ul className="text-sm text-blue-700 mt-1 space-y-1">
              <li>• Lorsqu'un ticket est créé, un email est envoyé au client</li>
              <li>• L'email contient un lien de suivi avec la référence du ticket</li>
              <li>• Si vous avez configuré une URL personnalisée, elle sera utilisée</li>
              <li>• Sinon, l'URL par défaut d'Izi Call Center sera utilisée</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};